@import 'fluid-icon.theme.less';

@import '../../styles/mixins/fluid-icon.mixin.less';
@import '../../styles/mixins/fluid-rotate-animation.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: inline-block;
  color: inherit;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-icon {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @icon-tokens: @icon-tokens-legacy;
      @fluid-icon();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @icon-tokens: @icon-tokens-lm;
      @fluid-icon();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @icon-tokens: @icon-tokens-corp;
      @fluid-icon();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @icon-tokens: @icon-tokens-portal;
      @fluid-icon();
    }
  }

  // == Embedded Popover

  .icon-popover {
    display: flex;
  }

  // == Full-Size Icon

  &(.fluid-icon-size-full) {
    display: block;
    height: 100%;
    max-height: 100%;
  }

}


// ================================================================ //
// -- Component Detached Ruleset

@fluid-icon: {
  @icon-xxs: @icon-sizes[xxs];
  @icon-xs: @icon-sizes[xs];
  @icon-sm: @icon-sizes[sm];
  @icon-md: @icon-sizes[md];
  @icon-lg: @icon-sizes[lg];
  @icon-xl: @icon-sizes[xl];

  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  // == Adornment

  &.icon-badge-container {

    .icon-badge {
      position: absolute;
      top: @icon-tokens[@adornment][offset];
      right: @icon-tokens[@adornment][offset];
      background-color: @icon-tokens[@adornment][color];
      border-radius: 50%;
      &.xxs {
        width: @icon-tokens[@adornment][@size][xxs];
        height: @icon-tokens[@adornment][@size][xxs];
      }
      &.xs {
        width: @icon-tokens[@adornment][@size][xs];
        height: @icon-tokens[@adornment][@size][xs];
      }
      &.sm {
        width: @icon-tokens[@adornment][@size][sm];
        height: @icon-tokens[@adornment][@size][sm];
      }
      &.md {
        width: @icon-tokens[@adornment][@size][md];
        height: @icon-tokens[@adornment][@size][md];
      }
      &.lg {
        width: @icon-tokens[@adornment][@size][lg];
        height: @icon-tokens[@adornment][@size][lg];
      }
      &.xl {
        width: @icon-tokens[@adornment][@size][xl];
        height: @icon-tokens[@adornment][@size][xl];
      }
    }
  }

  // == Rotation

  &.rotate {
    .fluid-rotate-infinite(1s);
  }

  // == Flip Vertical

  &.flipVertical {
    svg {
      transform: rotate(180deg);
    }
  }

  // == SVG Container

  i {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-style: normal;

    .transition(@property: fill; @duration: 100ms);

    svg {
      width: max-content;
      height: max-content;
      max-width: 100%;
      max-height: 100%;
      shape-rendering: geometricprecision;

      .icon-rotate();

      // Adds light gray border - apply directly to <svg> elements with white BGs
      &.svg-border {
        border: 1px solid @liberty-gray-10;
      }
    }
  }

  // == Icon Sizes & Scaling

  .iconSizes(@icon-xxs, @icon-xs, @icon-sm, @icon-md, @icon-lg, @icon-xl, 0);

  .create-scales();

  &.icon-size-full {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
  }

  // == Icon Colors

  .create-icon-color-profiles();

  .icon-color-red-orange {
    .color-fill(@color-red-orange)
  }

  .icon-color-deep-yellow {
    .color-fill(@color-deep-yellow)
  }

  .icon-color-opaque {
    opacity: 0.4;

    &.interactive:hover {
      opacity: 0.6;
    }
  }

  // == Inherit Icon Color (if "color" Prop undefined)

  .inherit-icon-color {
    color: inherit;
    fill: currentColor;

    // Apply directly to <svg> elements that require additional stroke (i.e. "border" for SVGs)
    .increase-line-weight {

      [stroke-width]:not([stroke]) {
        stroke: currentColor;
      }
    }
  }

  // == Interactive

  .interactive {
    cursor: pointer;
  }

  // == Disabled

  .disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
};


// ================================================================ //
// -- Mixins

// ==== Generate scale classes at 5% increments, and associated icon sizes.

.create-scales(@i: -100) when (@i < 100) {

  &.scale-@{i} {
    .iconSizes(@icon-xxs, @icon-xs, @icon-sm, @icon-md, @icon-lg, @icon-xl, @i);
  }

  .create-scales(@i + 5);
}

.iconSizes(@xxs, @xs, @sm, @md, @lg, @xl, @reduce: 0) {
  &.icon-size-xxs { .reduceSize(@reduce, @xxs) }
  &.icon-size-xs { .reduceSize(@reduce, @xs) }
  &.icon-size-sm { .reduceSize(@reduce, @sm) }
  &.icon-size-md { .reduceSize(@reduce, @md) }
  &.icon-size-lg { .reduceSize(@reduce, @lg) }
  &.icon-size-xl { .reduceSize(@reduce, @xl) }
}

.reduceSize(@percent, @size) {
  height: @size;
  width: calc(@size - ((@size / 100) * @percent));
  font-size: @size;
  line-height: @size;
}

// ==== Color Profiles

.create-icon-color-profiles() {
  @color-profiles: @icon-tokens[@map--color-profiles];

  each(@color-profiles, {
    @profile: @value; // @value = current object from loop iteration
    @name: @profile[@name];
    @fill-color: @profile[@fill-color];

    .icon-color-@{name} {
      .color-fill(@fill-color);
    }
  });
};
